import { getApiBaseUrl, handleResponse } from './client';
import { ReasoningEffort } from '@/types/api';

export interface Agent {
  id: string;
  created_at: Date;
  updated_at: Date;
  name: string;
  description: string | null;
  system_prompt_id: string | null;
  context_template_id: string | null;
  followup_context_template_id: string | null;
  model_id: string | null;
  reasoning_effort: ReasoningEffort | null;
  is_default: boolean;
  is_active: boolean;
  is_builtin: boolean;
  builtin_key: string | null;
  systemPrompt?: {
    id: string;
    prompt_name: string;
    prompt_content: string;
  };
  contextTemplate?: {
    id: string;
    template_name: string;
  };
  followupContextTemplate?: {
    id: string;
    template_name: string;
  };
}

export interface CreateAgentPayload {
  name: string;
  description?: string;
  system_prompt_id?: string | null;
  context_template_id?: string | null;
  followup_context_template_id?: string | null;
  model_id?: string | null;
  reasoning_effort?: ReasoningEffort | null;
  is_default?: boolean;
  is_active?: boolean;
}

export interface UpdateAgentPayload {
  name?: string;
  description?: string;
  system_prompt_id?: string | null;
  context_template_id?: string | null;
  followup_context_template_id?: string | null;
  model_id?: string | null;
  reasoning_effort?: ReasoningEffort | null;
  is_active?: boolean;
  is_default?: boolean;
}

export async function getAgents(activeOnly?: boolean): Promise<Agent[]> {
  const url = new URL(`${getApiBaseUrl()}/agents`);
  if (activeOnly) {
    url.searchParams.append('activeOnly', 'true');
  }
  const response = await fetch(url.toString());
  return handleResponse<Agent[]>(response);
}

export async function getAgent(id: string): Promise<Agent> {
  const response = await fetch(`${getApiBaseUrl()}/agents/${id}`);
  return handleResponse<Agent>(response);
}

export async function getDefaultAgent(): Promise<Agent | null> {
  const response = await fetch(`${getApiBaseUrl()}/agents/default`);
  if (response.status === 404) {
    return null;
  }
  return handleResponse<Agent>(response);
}

export async function createAgent(payload: CreateAgentPayload): Promise<Agent> {
  const response = await fetch(`${getApiBaseUrl()}/agents`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleResponse<Agent>(response);
}

export async function updateAgent(
  id: string,
  payload: UpdateAgentPayload,
): Promise<Agent> {
  const response = await fetch(`${getApiBaseUrl()}/agents/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleResponse<Agent>(response);
}

export async function deleteAgent(id: string): Promise<void> {
  const response = await fetch(`${getApiBaseUrl()}/agents/${id}`, {
    method: 'DELETE',
  });
  await handleResponse<void>(response);
}

export async function duplicateAgent(id: string): Promise<Agent> {
  const response = await fetch(`${getApiBaseUrl()}/agents/${id}/duplicate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  return handleResponse<Agent>(response);
}

export async function setAgentAsDefault(id: string): Promise<Agent> {
  const response = await fetch(`${getApiBaseUrl()}/agents/${id}/set-default`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  return handleResponse<Agent>(response);
}